import React, { useState, useEffect, useRef } from "react";
import "./limits.scss";
import { Button, ButtonSkeleton, SkeletonPlaceholder } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import LimitItem from "./LimitItem";
import { generateUID } from "../../../utils/generateUID";
import { useTranslation } from "react-i18next";

export default function Limits({
  areasData,
  networksData,
  setAreasData,
  isPending,
  currentArea,
  areasList,
  currentLimit,
  setCurrentLimit,
}) {
  const [currentAreaLimits, setCurrentAreaLimits] = useState([]);
  const [currentAreaGameTypeLimits, setCurrentAreaGameTypeLimits] = useState(
    {},
  );
  const [changingLimits, setChangingLimits] = useState({});
  const [changeAll, setChangeAll] = useState("");
  const [isModEnabled, setIsModEnabled] = useState();

  const { t } = useTranslation();
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    if (!isPending && areasData && Object.keys(areasData).length > 0) {
      const areaConfigs = areasData[currentArea].configs;
      setCurrentAreaLimits(areaConfigs);

      const currentAreaNetwork = areasData[currentArea].network;
      setCurrentAreaGameTypeLimits(
        networksData[currentAreaNetwork].gameType2AllowedBbCents,
      );

      const areaAutoModes = areasData[currentArea].autoModesEnabled;
      setIsModEnabled(areaAutoModes);
    }
  }, [currentArea, areasData, networksData, isPending]);

  const [validationState, setValidationState] = useState({});
  const isSaveAllInvalid = Object.values(validationState).some(
    (state) => state,
  );

  const handleValidationChange = (id, isInvalid) => {
    setValidationState((prev) => ({ ...prev, [id]: isInvalid }));
  };

  const handleAddLimit = () => {
    const { teamplayEditable, flowEditable, fprofilePowerEditable } =
      areasData[currentArea];

    const newObject = {
      gameType: "",
      bbCents: "",
      targetMode: "MANUAL",
      targetValue: 0,
      newLimit: true,
      id: generateUID(),
    };

    if (teamplayEditable) {
      newObject.teamplay = false;
    } else {
      newObject.teamplay = null;
    }

    if (flowEditable) {
      newObject.flow = false;
    } else {
      newObject.flow = null;
    }

    if (fprofilePowerEditable) {
      newObject.fprofilePower = 0;
    } else {
      newObject.fprofilePower = null;
    }

    const updatedArray = [...areasData[currentArea].configs, newObject];
    setAreasData((prevState) => ({
      ...prevState,
      [currentArea]: { ...prevState[currentArea], configs: updatedArray },
    }));

    setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: scrollContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const handleConfirmAllChanges = () => {
    setChangeAll("confirm");
  };

  const handleCancelAllChanges = () => {
    setChangeAll("cancel");
  };

  useEffect(() => {
    const changingLimitsObject = areasList.reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {});

    setChangingLimits(changingLimitsObject);
  }, [areasList]);

  return (
    <div className="limits">
      <div className="limits_title">
        <h4>{t("eco.limits.limitsTitle")}</h4>
        {changingLimits[currentArea]?.length ? (
          <div className="limits_title_buttons">
            <Button
              kind="tertiary"
              size="sm"
              onClick={handleConfirmAllChanges}
              disabled={isSaveAllInvalid}
            >
              {t("eco.limits.limitsButtonSaveAll")}
            </Button>
            <Button kind="ghost" size="sm" onClick={handleCancelAllChanges}>
              {t("eco.limits.limitsButtonCancelAll")}
            </Button>
          </div>
        ) : null}
      </div>
      {!isPending ? (
        <div className="limits_content" ref={scrollContainerRef}>
          {currentAreaLimits.map((obj) => (
            <LimitItem
              key={obj.id}
              config={obj}
              currentArea={currentArea}
              areasData={areasData}
              setAreasData={setAreasData}
              currentAreaLimits={currentAreaLimits}
              currentAreaGameTypeLimits={currentAreaGameTypeLimits}
              changingLimits={changingLimits}
              setChangingLimits={setChangingLimits}
              changeAll={changeAll}
              setChangeAll={setChangeAll}
              onValidationChange={handleValidationChange}
              isModEnabled={isModEnabled}
              currentLimit={currentLimit}
              setCurrentLimit={setCurrentLimit}
            />
          ))}
        </div>
      ) : (
        <SkeletonPlaceholder className="skeleton_placeholder" />
      )}
      {!isPending ? (
        <Button
          className="limits_add"
          renderIcon={Add}
          kind="ghost"
          onClick={handleAddLimit}
          disabled={
            areasData[currentArea]?.configs.some((obj) => obj.newLimit)
              ? true
              : false
          }
        >
          {t("eco.limits.limitsButtonAddLimit")}
        </Button>
      ) : (
        <ButtonSkeleton />
      )}
    </div>
  );
}
